import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { aaiApi } from '../../services/aaiApi';
import { getProjectsRoster } from '../../services/api';
import { useAsync } from '../aai/useAsync';
import type { AaiHome, AaiRequest, ParticipantHomeData, QuickWinList, ReadinessSnapshot } from '../../types/aai';
import { ReadinessDrawer, STAGE_LABEL, measuredLabel } from './ReadinessDrawer';
import {
  Action, Banner, Divided, EmptyState, ErrorNote, ListRow, Loading,
  PageBody, PageHead, Panel, PanelHead, RouteMark, StatusChip,
} from '../../components/aa';
import { Sparkles as ReadinessGlyph } from 'lucide-react';
import { QuickWinDrawer, RequestDrawer } from './WorkDrawers';
import { RoomHomePanel } from '../orbit-room/RoomHomePanel';
import { MyPart } from '../orbit-room/MyPart';
import '../orbit-room/room.css';
// import { HomeResults } from '../../components/HomeResults';
// import { ResultsBody } from './ResultsPage';
import type { QuickWin as QuickWinItem } from '../../types/aai';

/*
 * Home: your work.
 *
 * The guide's contract for this screen is that the most important next action
 * is visually obvious. That used to be read as "offer the next action first",
 * so the screen opened with a banner inviting a new request and the person's
 * own work sat below three other blocks.
 *
 * It is ordered by obligation now: anything actually stopped waiting on this
 * person, then their own work, and only then the invitations (ask Orbit, the
 * room, readiness, the next adoption step). Somebody who logs in to find out
 * where their work has got to should not have to scroll past an advert for
 * making more of it.
 *
 * Everything here is already-approved or already-theirs. No other person's
 * runs appear on this page at any point.
 */

// The first letter of the object's name, for its list mark.
const initialOf = (name: string): string => (name || '?').trim().charAt(0).toUpperCase();

const greeting = (hour: number): string => {
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

// The signed-in person's first name. `user_name` is what Settings writes and
// the sidebar reads, so this stays in step with an in-app profile edit.
const firstName = (): string => {
  const stored = localStorage.getItem('user_name') || '';
  const email = localStorage.getItem('user_email') || '';
  const source = stored || email.split('@')[0];
  const first = (source || '').split(/[\s._-]/)[0] || '';
  return first.charAt(0).toUpperCase() + first.slice(1);
};

const salutation = (): string => {
  const name = firstName();
  const hello = greeting(new Date().getHours());
  if (!name) return hello;
  return `${hello}, ${name}`;
};

const countLine = (readyWins: number, activeWork: number): string => {
  const wins = `${readyWins} Quick ${plural(readyWins, 'Win', 'Wins')} ready`;
  const work = `${activeWork} active ${plural(activeWork, 'request', 'requests')}`;
  return `${wins} · ${work}`;
};

function plural(n: number, one: string, many: string): string {
  if (n === 1) return one;
  return many;
}

// The status a requester actually cares about, in their own terms.
const REQUEST_STATE: Record<string, string> = {
  pending: 'pending',
  needs_info: 'needs_review',
  dept_approved: 'in_progress',
  approved: 'in_delivery',
  declined: 'declined',
};

const REQUEST_FOOT: Record<string, string> = {
  pending: 'Waiting on your department head',
  needs_info: 'Your department head asked for more detail',
  dept_approved: 'Approved by your department head, now with the AI champion',
  approved: 'Approved and in delivery',
  declined: 'Declined',
};

/*
 * Onboarding, or readiness. One strip, two states, the same way the Department
 * Head's Home does it: until a readiness baseline exists it asks for onboarding;
 * afterwards it shows the person's own stage and five dimensions. Nothing here
 * is a management report; the numbers are the person's and stay theirs.
 */
const ReadinessSection: React.FC<{ readiness: ReadinessSnapshot | null; onOpen: () => void }> = ({ readiness, onOpen }) => {
  const mark = (
    <RouteMark size="xl" className="bg-canvas-inset text-ink" icon={<ReadinessGlyph size={22} strokeWidth={2} />} />
  );
  if (!readiness) {
    // Parked for now: the readiness onboarding card.
    return null;
    /*
    return (
      <Banner
        className="mb-5"
        size="lg"
        mark={mark}
        kicker="Onboarding"
        title="Set your own readiness baseline"
        desc="A short Orbit conversation about how you work today. Your answers stay yours; managers only ever see protected aggregates."
        action={<Action size="lg" arrow to="/onboarding">Start onboarding</Action>}
      />
    );
    */
  }
  const stage = STAGE_LABEL[readiness.stage] || readiness.stage;
  return (
    <Banner
      className="mb-5"
      size="lg"
      mark={mark}
      kicker="My readiness"
      title="Your own AI readiness"
      desc={`${stage} stage · ${measuredLabel(readiness.measured_at)} · your five readiness dimensions, what improved, and the next step that fits your work.`}
      action={<Action size="lg" variant="quiet" arrow onClick={onOpen}>Open My Readiness</Action>}
    />
  );
};

// Parked on the Home for now; kept for when it comes back.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ReadyNow: React.FC<{ wins: QuickWinList; onOpen: (w: QuickWinItem) => void }> = ({ wins, onOpen }) => {
  const ready = wins.items.filter((w) => w.ready).slice(0, 4);
  return (
    <Panel floor>
      <PanelHead
        kicker="For you"
        title="Ready now"
        action={<Action variant="ghost" size="sm" to="/my-projects?tab=quick_wins">See all</Action>}
      />
      {ready.length === 0 && (
        <EmptyState
          title="Nothing is approved for you yet"
          reason="A Quick Win shows up here once it is approved for you or your department."
          action={<Action size="sm" arrow to="/orbit/request">Ask for one</Action>}
        />
      )}
      <Divided>
        {ready.map((w) => (
          <ListRow
            key={w.id}
            mark={<RouteMark kind="quick_win" glyph={initialOf(w.name)} />}
            title={w.name}
            desc={w.problem}
            right={<StatusChip status="ready" />}
            arrow
            onClick={() => onOpen(w)}
          />
        ))}
      </Divided>
    </Panel>
  );
};

// Parked on the Home for now; kept for when it comes back.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ActiveWork: React.FC<{ requests: AaiRequest[]; onOpen: (r: AaiRequest) => void }> = ({ requests, onOpen }) => (
  <Panel floor>
    <PanelHead
      kicker="My requests"
      title="Active work"
      action={<Action variant="ghost" size="sm" to="/my-projects">See all</Action>}
    />
    {requests.length === 0 && (
      <EmptyState
        title="Nothing in flight"
        reason="Requests you raise appear here with where they have got to and who has them."
      />
    )}
    <Divided>
      {requests.slice(0, 4).map((r) => (
        <ListRow
          key={r.id}
          mark={<RouteMark kind="project" glyph="" />}
          title={r.title}
          foot={REQUEST_FOOT[r.status] || r.status}
          right={<StatusChip status={REQUEST_STATE[r.status] || r.status} />}
          onClick={() => onOpen(r)}
        />
      ))}
    </Divided>
  </Panel>
);

/* A provider expert also builds for client companies. Home says which ones,
   so they never have to work out from a project list who they work for. */
export const ClientWorkCard: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Array<{ name: string; count: number }> | null>(null);
  useEffect(() => {
    let alive = true;
    getProjectsRoster()
      .then((r: any) => {
        const byName = new Map<string, number>();
        for (const p of (r?.projects || [])) {
          const name = (p.client_name || 'A client').trim();
          byName.set(name, (byName.get(name) || 0) + 1);
        }
        if (alive) setClients(Array.from(byName.entries()).map(([name, count]) => ({ name, count })));
      })
      .catch(() => { if (alive) setClients([]); });
    return () => { alive = false; };
  }, []);
  if (clients === null) return null;
  return (
    <Panel className="mb-5">
      <PanelHead
        kicker="Client work"
        title="You're working for"
        desc={clients.length > 0 ? 'The clients whose projects are assigned to you.' : 'No client project is assigned to you yet. Your company\'s Owner assigns them.'}
        action={clients.length > 0 && <Action size="sm" variant="quiet" arrow onClick={() => navigate('/my-projects?tab=client_work')}>Open client work</Action>}
      />
      {clients.length > 0 && (
        <Divided>
          {clients.map((c) => (
            <ListRow
              key={c.name}
              mark={<RouteMark glyph={c.name.charAt(0).toUpperCase()} size="lg" />}
              title={c.name}
              meta={[{ label: 'Projects', value: `${c.count} assigned to you` }]}
              onClick={() => navigate('/my-projects?tab=client_work')}
              arrow
            />
          ))}
        </Divided>
      )}
    </Panel>
  );
};

const EmployeeHome: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // Whatever the reader opened, held here so closing returns them to Home.
  const [openWin, setOpenWin] = useState<QuickWinItem | null>(null);
  const [readinessOpen, setReadinessOpen] = useState(false);

  // The old My Readiness address lands here with the drawer already open.
  useEffect(() => {
    if (params.get('open') !== 'readiness') return;
    setReadinessOpen(true);
    setParams({}, { replace: true });
  }, [params, setParams]);
  const [openRequest, setOpenRequest] = useState<AaiRequest | null>(null);
  const home = useAsync<AaiHome>(() => aaiApi.getHome(), 'Could not load your work.');
  const wins = useAsync<QuickWinList>(() => aaiApi.getMyQuickWins(), 'Could not load your Quick Wins.');

  if (home.loading) return <PageBody><Loading label="Loading your work" /></PageBody>;
  if (home.error || !home.data) {
    return <PageBody><ErrorNote message={home.error || 'Something went wrong.'} /></PageBody>;
  }

  // Every role's home resolves through the same endpoint. This screen is the
  // employee's, and reading another role's payload as if it were one would
  // silently show the wrong person's work.
  const participant = home.data as ParticipantHomeData;
  const requests: AaiRequest[] = participant.pending_requests || [];
  const readyCount = (wins.data?.items || []).filter((w) => w.ready).length;
  const isExpert = localStorage.getItem('is_expert') === '1';

  return (
    <PageBody>
      <PageHead
        eyebrow={salutation()}
        title="Your work"
        sub={countLine(readyCount, requests.length)}
      />

      {/* Parked for now: Ready now (Quick Wins) and Active work.
      <div className="grid gap-5 lg:grid-cols-2">
        {wins.data && <ReadyNow wins={wins.data} onOpen={setOpenWin} />}
        <ActiveWork requests={requests} onOpen={setOpenRequest} />
      </div>
      */}

      {isExpert && <ClientWorkCard />}


      {/* The questions that wait after the setup, answered whenever they like. */}
      <Panel className="mt-5">
        <PanelHead kicker="Orbit" title="Questions for you" desc="Questions about you and your own work. Nothing here blocks you; answer whenever you like." />
        <div className="oroom px-5 pb-5 md:px-6">
          <MyPart
            clientId={localStorage.getItem('client_id') || ''}
            onOpenDepartment={() => undefined}
            onAddDepartment={() => undefined}
            onInvite={() => undefined}
            onOpenTools={() => undefined}
            onOpenWork={(link) => navigate(link)}
          />
        </div>
      </Panel>

      <RoomHomePanel variant="member" />

      <ReadinessSection readiness={participant.readiness} onOpen={() => setReadinessOpen(true)} />

      {/* The AI Adoption next-step strip is hidden with the rest of the
          programme. /ai-adoption still resolves, so every link into a lesson
          keeps working; what went is the standing offer to open it. */}

      {openWin && <QuickWinDrawer item={openWin} onClose={() => setOpenWin(null)} />}
      {openRequest && <RequestDrawer request={openRequest} onClose={() => setOpenRequest(null)} />}
      {readinessOpen && <ReadinessDrawer onClose={() => setReadinessOpen(false)} />}
      {/* Results is hidden for now, the same as the other dashboards.
      <HomeResults
        ready={!home.loading}
        title="What AI returned to you"
        sub="Live results use reviewed evidence. Expected value remains a forecast until confirmed."
      >
        <ResultsBody />
      </HomeResults>
      */}
    </PageBody>
  );
};

export default EmployeeHome;
